// frontend/src/hooks/useMonthlyChallenge.js
// CODEX_PROMPT_83 : hook pour lire le defi mensuel actif depuis Supabase.
// Fallback sur les donnees statiques de monthlyChallenge.js si reseau
// indisponible, si aucune ligne ne correspond au mois courant, ou si la table
// n'existe pas encore en prod (ex. erreur PGRST204 avant migration).
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  MONTHLY_CHALLENGE,
  MONTHLY_CHALLENGE_KEY,
  monthlyChallengeText,
  isMonthlyChallengeComingSoon,
} from '../data/monthlyChallenge';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Calcule YYYY-MM depuis la date courante (cote client)
function getCurrentMonthKey() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

// Construit le libelle du mois (FR + EN) depuis une cle YYYY-MM
export function buildMonthLabel(monthKey) {
  const [year, month] = String(monthKey || '').split('-');
  const FR_MONTHS = [
    'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre',
  ];
  const EN_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const idx = parseInt(month, 10) - 1;
  if (idx < 0 || idx > 11) return { fr: monthKey, en: monthKey };
  return { fr: `${FR_MONTHS[idx]} ${year}`, en: `${EN_MONTHS[idx]} ${year}` };
}

// Transforme une ligne de la table monthly_challenges vers la structure
// attendue par les consommateurs existants (retrocompat avec MONTHLY_CHALLENGE).
export function rowToChallenge(row) {
  if (!row) return null;
  return {
    key: row.month_key,
    status: row.is_revealed ? 'active' : 'coming_soon',
    isComingSoon: !row.is_revealed,
    isRevealed: row.is_revealed,
    monthLabel: buildMonthLabel(row.month_key),
    label: MONTHLY_CHALLENGE.label, // libelle generique conserve depuis le fallback
    title: { fr: row.title_fr, en: row.title_en },
    subtitle: {
      fr: row.subtitle_fr || '',
      en: row.subtitle_en || '',
    },
    description: {
      fr: row.description_fr || '',
      en: row.description_en || '',
    },
    editorial: {
      fr: row.editorial_fr || '',
      en: row.editorial_en || '',
    },
    bonusPrompt: {
      fr: row.bonus_prompt_fr || '',
      en: row.bonus_prompt_en || '',
    },
    cta: MONTHLY_CHALLENGE.cta,
    homeSubtitle: {
      // homeSubtitle reutilise subtitle si defini, sinon description
      fr: row.subtitle_fr || row.description_fr || '',
      en: row.subtitle_en || row.description_en || '',
    },
    homeCta: MONTHLY_CHALLENGE.homeCta,
    paletteLabel: MONTHLY_CHALLENGE.paletteLabel,
    palette: [], // non migre : pas de champ en base
    image: row.image_url || null,
    tutorial: row.tutorial_url
      ? {
          // CODEX_PROMPT_86 (Bug B) : label generique (plus de nom hardcode type
          // "Tutoriel par Camille"). Le nom du createur est deja visible via le
          // sous-titre "Par @<tutorial_tiktok_username> sur TikTok" dans WheelPage.
          label: { fr: 'Tutoriel vidéo', en: 'Video tutorial' },
          tiktokUsername: row.tutorial_tiktok_username || '',
          url: row.tutorial_url,
        }
      : null,
    // challenge_theme : valeur de la colonne si non-null, sinon repli sur title_fr
    challengeTheme: row.challenge_theme || row.title_fr,
    winnerLabel: MONTHLY_CHALLENGE.winnerLabel,
    winnerByLevelLabel: MONTHLY_CHALLENGE.winnerByLevelLabel,
  };
}

// Cache module-level (partage entre les instances du hook pendant le cycle de
// vie de la page). 5 min de TTL via Date.now(), sans dependance externe.
let _cache = null;
let _cacheTs = 0;

// Helper de test : reinitialise le cache module-level entre deux tests Jest.
export function __resetMonthlyChallengeCache() {
  _cache = null;
  _cacheTs = 0;
}

export function useMonthlyChallenge() {
  const [challenge, setChallenge] = useState(MONTHLY_CHALLENGE); // fallback immediat
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Cache frais : ne pas refetch
      if (_cache && Date.now() - _cacheTs < CACHE_TTL_MS) {
        if (!cancelled) {
          setChallenge(_cache);
          setLoading(false);
        }
        return;
      }

      try {
        const currentMonthKey = getCurrentMonthKey();
        const { data, error: fetchError } = await supabase
          .from('monthly_challenges')
          .select('*')
          .eq('month_key', currentMonthKey)
          .maybeSingle();

        if (cancelled) return;

        if (fetchError) {
          // Echec reseau, Supabase, ou table inexistante (PGRST204 avant
          // migration) : on garde le fallback statique.
          // eslint-disable-next-line no-console
          console.warn('[useMonthlyChallenge] fetch failed, using static fallback', {
            message: fetchError.message,
          });
          setError(fetchError);
          setLoading(false);
          return;
        }

        if (data) {
          const resolved = rowToChallenge(data);
          _cache = resolved;
          _cacheTs = Date.now();
          setChallenge(resolved);
        }
        // Si data est null (aucune ligne pour ce mois), on garde MONTHLY_CHALLENGE
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('[useMonthlyChallenge] unexpected error, using static fallback', {
            message: err?.message,
          });
          setError(err);
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { challenge, loading, error };
}

export { MONTHLY_CHALLENGE_KEY, monthlyChallengeText, isMonthlyChallengeComingSoon };
